import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { paths } from '@/routes/paths';
import { useAuthStore } from '@/stores/useAuthStore';
import { Section } from '@/components/common/Section';
import { SectionCard } from '@/components/common/SectionCard';
import { SectionHeader } from '@/components/common/SectionHeader';
import {
  useCalibrationMatrix,
  useExportCalibrationMatrix,
} from '@/queries/useCalibrationMatrix';
import { useTalentCommittees } from '@/queries/useTalentCommittees';
import { useGeos, useSectors } from '@/queries/useLookups';
import { useDebounce } from '@/utils/useDebounce';
import type { CalibrationMatrixFilters } from '@/api/types/calibrationMatrix.types';
import { CalibrationMatrixFilterPanel } from './calibration-matrix/CalibrationMatrixFilterPanel';
import { CalibrationMatrixGrid } from './calibration-matrix/CalibrationMatrixGrid';
import { DistributionOfOutcomesSummary } from './calibration-matrix/DistributionOfOutcomesSummary';
import { CurrentDecisionsList } from './calibration-matrix/CurrentDecisionsList';
import { WindowLegend } from './calibration-matrix/WindowLegend';
import { formatGeosForTitle } from './calibration-matrix/helpers';
import { ANALYTICS_VALUE, CONSULTING_SECTORS } from './calibration-matrix/lookups';

const initialFilters: CalibrationMatrixFilters = {
  grade: null,
  analytics: null,
  geos: [],
  sector: null,
  talentCommitteeId: null,
};

export function CalibrationMatrixPage() {
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Staffing', 'HR']);

  const location = useLocation();
  const navigate = useNavigate();
  const hasConsumedNavStateRef = useRef(false);

  // Seed initial filters from navigation state if present (lazy initialiser so
  // it runs once per mount, avoiding setState-in-effect cascades). The history
  // entry is then dropped in the effect below so a manual reload doesn't
  // re-apply the prefill.
  const [filters, setFilters] = useState<CalibrationMatrixFilters>(() => {
    const state = location.state as { prefill?: CalibrationMatrixFilters } | null;
    return state?.prefill ?? initialFilters;
  });

  useEffect(() => {
    if (hasConsumedNavStateRef.current) return;
    const state = location.state as { prefill?: CalibrationMatrixFilters } | null;
    if (state?.prefill) {
      hasConsumedNavStateRef.current = true;
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const debouncedFilters = useDebounce(filters, 300);

  const { data: tcs } = useTalentCommittees();
  const tcOptions = useMemo(
    () => (tcs ?? []).map((tc) => ({ id: tc.id, name: tc.name })),
    [tcs]
  );

  const { data: geos } = useGeos();
  const { data: sectorsFromApi } = useSectors();
  const geoOptions = useMemo(() => geos ?? [], [geos]);
  const sectorOptions = useMemo(() => {
    const fromApi = new Set(
      (sectorsFromApi ?? []).map((s) => s.toLowerCase())
    );
    return CONSULTING_SECTORS.filter(
      (s) => s === ANALYTICS_VALUE || fromApi.has(s.toLowerCase())
    );
  }, [sectorsFromApi]);

  const matrixQuery = useCalibrationMatrix(debouncedFilters);
  const exportMutation = useExportCalibrationMatrix();

  const currentTitle = useMemo(() => {
    const grade = filters.grade ?? '—';
    const geo = formatGeosForTitle(filters.geos);
    const sector = filters.sector ?? 'All';
    return `Matrix for Grade: ${grade}, GEO: ${geo}, Sector: ${sector}`;
  }, [filters]);

  const handleExport = () => {
    if (!matrixQuery.data) return;
    exportMutation.mutate(debouncedFilters);
  };

  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  const matrix = matrixQuery.data;
  const visibilityCaption = matrix
    ? `${matrix.percentVisible.percentage}% people visible`
    : undefined;

  const isBackgroundUpdating =
    matrixQuery.isFetching && !matrixQuery.isPending;

  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 3 }}
      >
        Calibration Matrix
      </Typography>

      <Section>
        <SectionHeader>Filters</SectionHeader>
        <SectionCard>
          <CalibrationMatrixFilterPanel
            filters={filters}
            onChange={setFilters}
            geosOptions={geoOptions}
            sectorsOptions={sectorOptions}
            tcOptions={tcOptions}
            visibilityCaption={visibilityCaption}
            isUpdating={isBackgroundUpdating}
          />
        </SectionCard>
      </Section>

      {matrix && (
        <>
          <Section>
            <SectionHeader>{currentTitle}</SectionHeader>
            <SectionCard>
              {matrixQuery.isPending ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Stack spacing={3}>
                  <WindowLegend windows={matrix.gradeConfig.windows} />
                  <CalibrationMatrixGrid
                    gradeConfig={matrix.gradeConfig}
                    consultants={matrix.consultants}
                  />
                  <DistributionOfOutcomesSummary distribution={matrix.distribution} />
                </Stack>
              )}
            </SectionCard>
          </Section>

          <Section>
            <SectionHeader>Current Decisions</SectionHeader>
            <SectionCard>
              <Box sx={{ position: 'relative' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleExport}
                  disabled={!matrix || exportMutation.isPending}
                  aria-disabled={!matrix || exportMutation.isPending}
                  data-testid="export-to-hr-button"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                  }}
                >
                  Export to HR
                </Button>
                <CurrentDecisionsList decisions={matrix.decisions} />
              </Box>
            </SectionCard>
          </Section>
        </>
      )}
    </Box>
  );
}

export default CalibrationMatrixPage;
